import { useEffect, useRef } from 'react';
import { useRevealOnScroll } from '@/hooks/useRevealOnScroll';

export default function HowWeGenerate() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const handleScroll = () => {
      const rect = section.getBoundingClientRect();
      const progress = Math.max(0, Math.min(1, 1 - (rect.top / window.innerHeight)));
      section.style.setProperty('--scroll-progress', String(progress));
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useRevealOnScroll(sectionRef);

  const steps = [
    {
      id: 1,
      title: 'Generate, don\'t configure',
      description:
        'Software shaped to your operation, not a product you adapt to.',
      icon: 'ri-code-box-line',
      image: 'https://public.readdy.ai/ai/img_res/edited_e4e08a48d782b6dc1575a9a0f43ee742_aff16d30.jpg',
      imageAlt: 'Modern office workspace with custom software development',
    },
    {
      id: 2,
      title: 'AI from the first line',
      description:
        'Generative capability built into the system, not bolted on after.',
      icon: 'ri-robot-2-line',
      image: 'https://readdy.ai/api/search-image?query=Modern%20technology%20workspace%20with%20sleek%20monitor%20displaying%20elegant%20data%20visualization%2C%20warm%20ambient%20lighting%2C%20clean%20professional%20desk%20setup%2C%20realistic%20editorial%20photography%20with%20soft%20depth%20of%20field%20and%20warm%20neutral%20tones%2C%20high%20detail%2C%20sophisticated%20composition&width=800&height=500&seq=how-we-generate-real-02&orientation=landscape',
      imageAlt: 'Modern technology workspace with AI-powered data visualization',
    },
    {
      id: 3,
      title: 'Built to own',
      description:
        'You get the system and the code behind it, not just a licence.',
      icon: 'ri-git-repository-line',
      image: 'https://readdy.ai/api/search-image?query=Modern%20glass%20office%20building%20exterior%20with%20warm%20golden%20hour%20sunlight%20reflecting%20off%20windows%2C%20architectural%20photography%20with%20clean%20lines%20and%20geometric%20forms%2C%20realistic%20editorial%20style%20with%20warm%20amber%20tones%20and%20natural%20lighting%2C%20high%20detail%2C%20professional%20composition%20representing%20permanence%20and%20ownership&width=800&height=500&seq=how-we-generate-real-03&orientation=landscape',
      imageAlt: 'Modern glass office building representing code ownership and permanence',
    },
  ];

  return (
    <section
      id="how"
      ref={sectionRef}
      className="relative w-full min-h-[80vh] md:min-h-screen flex items-center overflow-hidden bg-background-50"
    >
      {/* Section transition marker */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-center gap-3 z-20">
        <div className="w-10 h-[1px] bg-gradient-to-r from-transparent to-accent-500/20"></div>
        <div className="w-1 h-1 rounded-full bg-accent-500/25"></div>
        <div className="w-10 h-[1px] bg-gradient-to-l from-transparent to-accent-500/20"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 w-full px-6 md:px-12 py-24 md:py-32">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-20">
            {/* Eyebrow */}
            <div data-reveal className="flex items-center justify-center gap-3 mb-8">
              <div className="w-8 h-[1px] bg-accent-500/40"></div>
              <span className="text-xs font-label tracking-[0.3em] uppercase text-accent-500">
                Our Approach
              </span>
              <div className="w-8 h-[1px] bg-accent-500/40"></div>
            </div>

            <h2 data-reveal data-reveal-delay="0.1" className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-foreground-900 tracking-tight leading-[1.12] max-w-3xl mx-auto">
              We start from a blank page
              <br className="hidden md:block" />
              and <em className="italic">your problem.</em>
            </h2>

            <p data-reveal data-reveal-delay="0.18" className="mt-6 font-heading text-lg md:text-xl text-foreground-500 tracking-tight leading-relaxed max-w-2xl mx-auto">
              <span className="text-accent-500 font-semibold">Not a template</span>
              {' '}you need to adjust.
            </p>
            <p data-reveal data-reveal-delay="0.2" className="mt-6 text-foreground-700 max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
              Every system is generated around how your business works, with AI built in from the start rather than added later.
            </p>
          </div>

          {/* Steps grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {steps.map((step, index) => (
              <div
                key={step.id}
                data-reveal
                data-reveal-delay={`${0.3 + index * 0.1}`}
                className="group relative p-8 md:p-10 rounded-2xl border border-foreground-300/10 bg-background-200/50 backdrop-blur-sm hover:border-accent-500/25 transition-all duration-500 overflow-hidden"
              >
                {/* Card hover glow */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                  <div className="absolute -inset-1 bg-gradient-to-br from-accent-500/4 via-transparent to-transparent rounded-2xl"></div>
                </div>

                {/* Step image */}
                <div className="relative w-full aspect-[16/11] mb-6 rounded-xl overflow-hidden bg-background-100">
                  <img
                    src={step.image}
                    alt={step.imageAlt}
                    className="w-full h-full object-cover object-top group-hover:scale-[1.03] transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background-200/40 via-transparent to-transparent pointer-events-none"></div>
                </div>

                {/* Step icon */}
                <div className="mb-5 w-12 h-12 rounded-full bg-accent-500/8 border border-accent-500/15 flex items-center justify-center group-hover:bg-accent-500/12 group-hover:border-accent-500/25 transition-all duration-500">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${step.icon} text-accent-500 text-lg`}></i>
                  </div>
                </div>

                {/* Step content */}
                <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                  {step.title}
                </h3>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  {step.description}
                </p>

                {/* Bottom accent line */}
                <div className="mt-8 w-full h-[1px] bg-gradient-to-r from-accent-500/20 via-accent-500/5 to-transparent group-hover:from-accent-500/40 group-hover:via-accent-500/15 transition-all duration-500"></div>
              </div>
            ))}
          </div>

          {/* Bottom separator */}
          <div className="mt-20 flex items-center justify-center gap-4">
            <div className="w-12 h-[1px] bg-foreground-400/20"></div>
            <div className="w-1.5 h-1.5 rounded-full bg-accent-500/40"></div>
            <div className="w-12 h-[1px] bg-foreground-400/20"></div>
          </div>
        </div>
      </div>
    </section>
  );
}